import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import formatDateShort from "@/lib/dateFormatter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DollarSign,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  User,
  Clock,
  FileText,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function AdminSalesReports() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    client_filter: "",
    payment_filter: "",
    date_from: "",
    date_to: "",
  });
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    payment_methods: [],
  });
  const [showFilters, setShowFilters] = useState(false);
  // const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchLogs();
    fetchFilterOptions();
  }, [pagination.page, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        page: pagination.page,
        per_page: pagination.per_page,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
      });

      const res = await axios.get(`${API}/sales-reports?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Fetched logs:", res.data);
      setLogs(res.data.data);
      setPagination((prev) => ({
        ...prev,
        total: res.data.total,
        total_pages: res.data.total_pages,
      }));
    } catch (error) {
      toast.error("Failed to fetch activity logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/sales-reports/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFilterOptions(res.data);
    } catch (error) {
      console.error("Failed to fetch filter options");
    }
  };

  const getPaymentColor = (role) => {
    switch (role) {
      case "cash":
        return "bg-green-100 text-green-800";
      case "gcash":
        return "bg-blue-100 text-blue-800";
      case "bank_transfer":
        return "bg-orange-100 text-orange-800";
      case "others":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const handleClearFilters = () => {
    setFilters({
      client_filter: "",
      payment_filter: "",
      date_from: "",
      date_to: "",
    });
  };

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-slate-900"
              data-testid="sales-reports-title"
            >
              Sales Reports
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Monitor all sales activities and user actions
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6" data-testid="filters-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    From Date
                  </label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        date_from: e.target.value,
                      }))
                    }
                    className="mt-1"
                    data-testid="filter-date-from"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    To Date
                  </label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        date_to: e.target.value,
                      }))
                    }
                    className="mt-1"
                    data-testid="filter-date-to"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Client/User
                  </label>
                  <Select
                    value={filters.client_filter}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        client_filter: value === "all" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="filter-client">
                      <SelectValue placeholder="All Clients" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {filterOptions.clients.map((client) => (
                        <SelectItem key={client} value={client}>
                          {client}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Payment Method
                  </label>
                  <Select
                    value={filters.payment_filter}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        payment_filter: value === "all" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger
                      className="mt-1"
                      data-testid="filter-payment-method"
                    >
                      <SelectValue placeholder="All Payment Methods" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Payment Methods</SelectItem>
                      {filterOptions.payment_methods.map((method) => (
                        <SelectItem key={method} value={method}>
                          {method}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  data-testid="clear-filters-btn"
                >
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <Card>
            <CardContent className="p-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">Cash </p>
                  <p className="text-xs text-slate-500">{pagination.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">GCash</p>
                  <p className="text-xs text-slate-500">
                    {filterOptions.users.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">Bank</p>
                  <p className="text-xs text-slate-500">
                    {filterOptions.modules.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">Other</p>
                  <p className="text-xs text-slate-500">
                    {filterOptions.actions.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div> */}

        {/* <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, account number, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div> */}
        {/* Logs Table */}
        <Card>
          <CardContent className="p-0 overflow-hidden flex flex-col h-[calc(100vh-100px)] sm:h-[calc(100vh-64px-200px)]">
            <div className="overflow-auto flex-1">
              <table className="w-full" data-testid="activity-logs-table">
                <thead className="border-b border-slate-200 sticky top-0 bg-blue-100 z-10">
                  <tr className="border-b bg-slate-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      ACCOUNT NUMBER
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 hidden sm:table-cell md:table-cell">
                      CLIENT NAME/DETAILS
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 hidden sm:table-cell md:table-cell">
                      PAYMENT METHOD
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 hidden sm:table-cell md:table-cell">
                      REFERENCE NUMBER
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 hidden sm:table-cell md:table-cell">
                      NOTES
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 hidden sm:table-cell md:table-cell">
                      AMOUNT
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 hidden sm:table-cell md:table-cell">
                      DATE
                    </th>
                    {/* <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Record
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Details
                    </th> */}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center text-slate-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center text-slate-500"
                      >
                        No sales reports found
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr
                        key={log.id}
                        className="hover:bg-slate-50"
                        data-testid={`log-row-${log.id}`}
                      >
                        <td className="px-4 py-3 sm:px-6">
                          {/* <div className="flex items-center gap-2 sm:flex-row sm:flex-wrap"> */}
                          <span className="text-sm font-medium text-slate-900 hidden sm:inline md:inline">
                            {log.account_number || "-"}
                          </span>
                          <br />
                          <span className="text-sm font-medium text-slate-900">
                            Client Name: {log.client_name || "-"}
                          </span>
                          <p className="text-xs text-slate-500 sm:hidden">
                            Account Number: {log.account_number || "-"}
                          </p>
                          <p className="text-xs text-slate-500 sm:hidden">
                            Payment Method: {log.payment_method || "-"}
                          </p>
                          <p className="text-xs text-slate-500 sm:hidden">
                            Reference Number: {log.reference_number || "-"}
                          </p>
                          <p className="text-xs text-slate-500 sm:hidden">
                            Notes: {log.notes || "-"}
                          </p>
                          <p className="text-xs text-slate-700 font-semibold sm:hidden">
                            Amount: {parseFloat(log.amount || "0").toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500 sm:hidden">
                            Date: {formatDateShort(log.created_at)}
                          </p>
                          {/* </div> */}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell md:table-cell">
                          <span className="text-sm font-medium text-slate-900">
                            {log.client_name || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell md:table-cell">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getPaymentColor(log.payment_method)}`}
                          >
                            {log.payment_method}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell md:table-cell">
                          <span className="text-sm text-slate-700 capitalize">
                            {log.reference_number || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell md:table-cell">
                          <span className="text-sm text-slate-700 capitalize">
                            {log.notes || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell md:table-cell">
                          <span className="text-sm text-slate-700 capitalize">
                            {parseFloat(log.amount || "0").toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell md:table-cell">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-700 whitespace-nowrap">
                              {formatDateShort(log.created_at)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-20 block bg-[#f9f9f9] lg:hidden">
              <span> </span>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-4 py-3 border-t items-center justify-between sticky bottom-0 hidden lg:flex">
                <p className="text-sm text-slate-600">
                  Showing {(pagination.page - 1) * pagination.per_page + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.per_page,
                    pagination.total,
                  )}{" "}
                  of {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.total_pages}
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="fixed bottom-0 left-0 w-full bg-white border-t shadow-md z-40 block lg:hidden">
              {pagination.total_pages > 1 && (
                <div className="border-t bg-white px-4 py-3 flex items-center justify-between sticky bottom-0">
                  <p className="text-sm text-slate-600">
                    Showing {(pagination.page - 1) * pagination.per_page + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.per_page,
                      pagination.total,
                    )}{" "}
                    of {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          page: prev.page - 1,
                        }))
                      }
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === pagination.total_pages}
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          page: prev.page + 1,
                        }))
                      }
                      data-testid="next-page-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
